/**
 * Cached version of fetchTolgeeTranslations
 *
 * This wrapper checks the cache before making API calls to Tolgee.
 * Significantly reduces network requests and improves language switch performance.
 */

import { fetchTolgeeTranslations } from "./fetchTranslations";
import { translationsCache } from "./translationsCache";
import { TOLGEE_REMOTE_ENABLED } from "./config";


type TolgeeTranslations = Record<string, string>;

interface FetchTranslationsCachedOptions {
  language: string;
  bypassCache?: boolean; // For testing/debugging - forces fresh API call
  isRetry?: boolean; // Флаг retry попытки для правильного timeout
}

export async function fetchTolgeeTranslationsCached(
  options: FetchTranslationsCachedOptions
): Promise<TolgeeTranslations> {
  if (!TOLGEE_REMOTE_ENABLED) return {};

  const { language, bypassCache = false, isRetry = false } = options;

  const cacheEnabled =
    process.env.NEXT_PUBLIC_TOLGEE_CACHE_ENABLED !== "false";

  if (cacheEnabled && !bypassCache) {
    const cached = translationsCache.get(language);
    if (cached) {
      return cached;
    }
  }

  const translations = await fetchTolgeeTranslations({
    language,
    isRetry,
  });

  const resultKeyCount = Object.keys(translations).length;

  if (cacheEnabled && resultKeyCount > 0) {
    translationsCache.set(language, translations);
  } else if (resultKeyCount === 0) {
    console.warn(`[Tolgee DEBUG Cached] Got EMPTY result for "${language}" — will NOT cache`);
  }

  return translations;
}

export { translationsCache };

// Expose cache to window in development for debugging
if (
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined"
) {
  (window as any).__tolgeeCache = translationsCache;
}
